import type { CSVImportResult, Transaction } from '@finapp/types';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { type BulkEditData, BulkEditModal } from '../components/BulkEditModal';
import CsvUpload from '../components/CsvUpload';
import { TransactionEditModal } from '../components/TransactionEditModal';
import TransactionCardView from '../components/transactions/TransactionCardView';
import TransactionTableView from '../components/transactions/TransactionTableView';
import { apiUrl, fetchWithAuth } from '../config/api';
import useTransactionData from '../hooks/useTransactionData';

interface LocationState {
  message?: string;
}

const DEFAULT_PAGE_SIZE = 50;

const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const Transactions: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [message, setMessage] = useState<string | null>(null);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);

  // Multi-select state
  const [selectedTransactions, setSelectedTransactions] = useState<Set<number>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Bulk edit modal state
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditing, setBulkEditing] = useState(false);

  // Pagination + year/month/search filter state. Changing a filter resets to page 1, since
  // staying on e.g. page 5 of a now-much-smaller filtered result would just show an empty page.
  const [page, setPage] = useState(1);
  const [year, setYear] = useState<number | undefined>(undefined);
  const [month, setMonth] = useState<number | undefined>(undefined);
  // searchInput is the raw, immediate input value (updates every keystroke); `search` is the
  // debounced value actually sent to the API, so typing doesn't trigger a fetch per keystroke.
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState<string | undefined>(undefined);

  const currentYear = new Date().getFullYear();
  // Same "last 6 years" convention as Forecast.tsx's year selector.
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const handleYearChange = (value: string) => {
    const newYear = value === '' ? undefined : Number.parseInt(value, 10);
    setYear(newYear);
    // A month filter without a year is invalid (the API 400s), so clear it too.
    if (newYear === undefined) {
      setMonth(undefined);
    }
    setPage(1);
  };

  const handleMonthChange = (value: string) => {
    setMonth(value === '' ? undefined : Number.parseInt(value, 10));
    setPage(1);
  };

  // Debounce searchInput -> search so typing doesn't trigger a fetch per keystroke (same
  // setTimeout/clearTimeout convention as TransactionEditModal's autosave debounce). Resets
  // the page back to 1 whenever the debounce actually fires, mirroring
  // handleYearChange/handleMonthChange's reset-on-filter-change behavior.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(searchInput.trim() || undefined);
      setPage(1);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Stable reference across renders - only changes when page/year/month/search actually
  // change - so useTransactionData's internal useCallback/useEffect don't re-fetch on every
  // unrelated re-render of this component (e.g. selecting a row, opening a modal).
  const transactionFilter = useMemo(
    () => ({ page, limit: DEFAULT_PAGE_SIZE, year, month, search }),
    [page, year, month, search]
  );

  const {
    transactions,
    drafts,
    userRole,
    isLoading: loadingTransactions,
    reload,
    setTransactions,
    total = 0,
    totalPages = 0,
  } = useTransactionData(transactionFilter);

  // Show message from navigation state
  useEffect(() => {
    const state = location.state as LocationState;
    if (state?.message) {
      setMessage(state.message);
      // Clear the state
      window.history.replaceState({}, document.title);

      // Auto-hide message after 5 seconds
      setTimeout(() => setMessage(null), 5000);
    }
  }, [location.state]);

  // Multi-select handlers
  const handleSelectTransaction = (transactionId: number) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
    } else {
      newSelected.add(transactionId);
    }
    setSelectedTransactions(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  const handleSelectAll = () => {
    if (selectedTransactions.size === transactions.length) {
      // Deselect all
      setSelectedTransactions(new Set());
      setShowBulkActions(false);
    } else {
      // Select all
      const allIds = new Set(transactions.map((t) => t.id));
      setSelectedTransactions(allIds);
      setShowBulkActions(true);
    }
  };

  const handleClearSelection = () => {
    setSelectedTransactions(new Set());
    setShowBulkActions(false);
  };

  // Unified bulk edit handler
  const handleBulkEdit = async (data: BulkEditData) => {
    setBulkEditing(true);
    const operations: string[] = [];

    try {
      // Handle costpoint assignment if enabled
      if (data.costpoint) {
        const transactionIdsArray = Array.from(selectedTransactions);
        console.log('Bulk edit - selected transactions:', transactionIdsArray);
        console.log('Bulk edit - costpoint data:', {
          costpoint: data.costpoint,
          amount: data.costpointAmount,
          mode: data.costpointMode,
        });
        console.log('Bulk edit - request payload:', {
          transactionIds: transactionIdsArray,
          costpoint: data.costpoint,
          amount: data.costpointAmount || 0,
        });

        const response = await fetchWithAuth(apiUrl('transactions/batch/costpoints'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            transactionIds: transactionIdsArray,
            costpoint: data.costpoint,
            amount: data.costpointAmount || 0,
          }),
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            operations.push(
              `assigned ${data.costpoint} costpoint to ${
                result.updated
              } transaction${result.updated !== 1 ? 's' : ''}`
            );
          }
        } else {
          throw new Error('Failed to assign costpoints');
        }
      }

      // Handle tag operations if enabled
      if (data.tagIds && data.tagIds.length > 0 && data.tagOperation) {
        const tagPayload = {
          transaction_ids: Array.from(selectedTransactions),
          tag_ids: data.tagIds,
          operation: data.tagOperation,
        };
        console.log('Bulk edit - tag request payload:', tagPayload);

        const response = await fetchWithAuth(apiUrl('tags/bulk'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(tagPayload),
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            const operationText =
              data.tagOperation === 'add'
                ? 'added'
                : data.tagOperation === 'remove'
                  ? 'removed'
                  : 'replaced';
            operations.push(
              `${operationText} tags ${
                data.tagOperation === 'add' ? 'to' : data.tagOperation === 'remove' ? 'from' : 'for'
              } ${selectedTransactions.size} transaction${
                selectedTransactions.size !== 1 ? 's' : ''
              }`
            );
          }
        } else {
          throw new Error('Failed to update tags');
        }
      }

      if (operations.length > 0) {
        setMessage(`Successfully ${operations.join(' and ')}`);

        // Clear selection
        handleClearSelection();
      }
    } catch (error) {
      console.error('Error in bulk edit:', error);
      setMessage('Failed to apply bulk changes. Please try again.');
    } finally {
      setBulkEditing(false);
    }
  };

  const handleEditTransaction = (transaction: Transaction) => {
    setEditingTransaction(transaction);
  };

  const handleUpdateTransaction = async (updatedTransaction: Partial<Transaction>) => {
    if (!editingTransaction) return;

    const response = await fetchWithAuth(apiUrl(`transactions/${editingTransaction.id}`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatedTransaction),
    });

    if (response.ok) {
      const updated = await response.json();
      setTransactions((prev) => prev.map((t) => (t.id === editingTransaction.id ? updated : t)));

      // Also update the editingTransaction state to ensure the modal receives the fresh data
      setEditingTransaction(updated);

      // Don't show toast here - let the modal handle it
      // Don't close modal automatically - let user continue editing
    } else {
      // Throw error with status for proper error handling in modal
      const error = Object.assign(new Error('Failed to update transaction'), {
        status: response.status,
      });
      throw error;
    }
  };

  const handleDeleteTransaction = async (transactionId: number) => {
    try {
      const response = await fetchWithAuth(apiUrl(`transactions/${transactionId}`), {
        method: 'DELETE',
      });

      if (response.ok) {
        setTransactions((prev) => prev.filter((t) => t.id !== transactionId));
        setEditingTransaction(null);
        toast.success('Transaction deleted successfully!', {
          position: 'bottom-right',
          autoClose: 3000,
        });
      } else {
        throw new Error('Failed to delete transaction');
      }
    } catch (error) {
      console.error('Failed to delete transaction:', error);
      toast.error('Failed to delete transaction. Please try again.', {
        position: 'bottom-right',
        autoClose: 4000,
      });
    }
  };

  const handleTransactionUpdate = (updatedTransaction: Transaction) => {
    setTransactions((prev) =>
      prev.map((t) => (t.id === updatedTransaction.id ? updatedTransaction : t))
    );
  };

  const handleUploadSuccess = (result: CSVImportResult & { draftId?: string }) => {
    console.log('CSV uploaded successfully:', result);

    // Navigate to the draft page if a draft ID is returned
    if (result.draftId) {
      navigate(`/import-draft/${result.draftId}`);
    } else {
      // Fallback: refresh the data
      reload();
    }
  };

  return (
    <div>
      {/* Success/Info Message */}
      {message && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-green-800">{message}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setMessage(null)}
                className="inline-flex bg-green-50 rounded-md p-1.5 text-green-500 hover:bg-green-100"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="mb-4 sm:mb-0">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Transactions</h1>
            <p className="mt-2 text-sm text-gray-700">
              Manage and categorize your financial transactions
            </p>
          </div>
          <div className="w-full sm:w-auto">
            <CsvUpload
              onUploadSuccess={handleUploadSuccess}
              onUploadError={(error) => {
                console.error('CSV upload failed:', error);
              }}
            />
          </div>
        </div>
      </div>

      {/* Year/Month filters - same selector style as Forecast.tsx's year selector */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="flex flex-col">
            <label
              htmlFor="transactions-year-select"
              className="text-sm font-medium text-gray-700 mb-1"
            >
              Year
            </label>
            <select
              id="transactions-year-select"
              value={year ?? ''}
              onChange={(e) => handleYearChange(e.target.value)}
              disabled={loadingTransactions}
              className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">All years</option>
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label
              htmlFor="transactions-month-select"
              className="text-sm font-medium text-gray-700 mb-1"
            >
              Month
            </label>
            <select
              id="transactions-month-select"
              value={month ?? ''}
              onChange={(e) => handleMonthChange(e.target.value)}
              disabled={loadingTransactions || year === undefined}
              className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">All months</option>
              {MONTH_LABELS.map((label, index) => (
                <option key={label} value={index + 1}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col flex-1 min-w-0 sm:max-w-xs">
            <label
              htmlFor="transactions-search-input"
              className="text-sm font-medium text-gray-700 mb-1"
            >
              Search
            </label>
            <input
              id="transactions-search-input"
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Description or heading..."
              className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>
        </div>
      </div>

      {/* Draft Management Section */}
      {drafts.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Import Drafts</h2>
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {drafts.map((draft) => (
                <li key={draft.id}>
                  <div className="px-4 py-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center">
                      <div
                        className={`flex-shrink-0 w-3 h-3 rounded-full ${
                          draft.status === 'ready'
                            ? 'bg-green-500'
                            : draft.status === 'draft'
                              ? 'bg-yellow-500'
                              : 'bg-gray-500'
                        }`}
                      />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{draft.filename}</div>
                        <div className="text-sm text-gray-500">
                          {draft.validation_summary.complete_transactions} of{' '}
                          {draft.validation_summary.total_transactions} transactions complete
                          {draft.validation_summary.total_errors > 0 && (
                            <span className="text-red-600 ml-2">
                              • {draft.validation_summary.total_errors} error
                              {draft.validation_summary.total_errors !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          draft.status === 'ready'
                            ? 'bg-green-100 text-green-800'
                            : draft.status === 'draft'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {draft.status.toUpperCase()}
                      </span>
                      <button
                        onClick={() => navigate(`/import-draft/${draft.id}`)}
                        className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                      >
                        Continue →
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Bulk Actions Toolbar */}
      {showBulkActions && (
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-900">
                {selectedTransactions.size} transaction
                {selectedTransactions.size !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={handleClearSelection}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear selection
              </button>
            </div>
            <div className="flex items-center space-x-3">
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setShowBulkEditModal(true)}
                disabled={bulkEditing}
              >
                {bulkEditing ? 'Applying Changes...' : 'Bulk Edit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transactions List - Mobile and Desktop */}
      <div className="mt-6">
        {loadingTransactions ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-sm text-gray-600">Loading transactions...</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg
                className="mx-auto h-16 w-16"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {year !== undefined || search !== undefined
                ? 'No transactions match these filters'
                : 'No transactions yet'}
            </h3>
            <p className="text-gray-500 mb-4">
              {year !== undefined || search !== undefined
                ? 'Try a different year, month, or search term.'
                : 'Upload a CSV file to get started.'}
            </p>
          </div>
        ) : (
          <>
            <TransactionTableView
              transactions={transactions}
              selectedItems={selectedTransactions}
              userRole={userRole}
              onEdit={handleEditTransaction}
              onDelete={handleDeleteTransaction}
              onSelect={handleSelectTransaction}
              onSelectAll={handleSelectAll}
            />
            <TransactionCardView
              transactions={transactions}
              selectedItems={selectedTransactions}
              userRole={userRole}
              onEdit={handleEditTransaction}
              onDelete={handleDeleteTransaction}
              onSelect={handleSelectTransaction}
            />

            {/* Pagination controls */}
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 bg-white px-4 py-3 rounded-md shadow-sm border border-gray-200">
              <p className="text-sm text-gray-700">
                Showing page {page} of {Math.max(totalPages, 1)} ({total} transaction
                {total !== 1 ? 's' : ''} total)
              </p>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || loadingTransactions}
                  className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(Math.max(totalPages, 1), p + 1))}
                  disabled={page >= totalPages || loadingTransactions}
                  className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Transaction Edit Modal */}
      <TransactionEditModal
        transaction={editingTransaction}
        isOpen={!!editingTransaction}
        onClose={() => setEditingTransaction(null)}
        onSave={handleUpdateTransaction}
        onTransactionUpdate={handleTransactionUpdate}
      />

      {/* Bulk Edit Modal */}
      <BulkEditModal
        isOpen={showBulkEditModal}
        onClose={() => setShowBulkEditModal(false)}
        onSave={handleBulkEdit}
        selectedCount={selectedTransactions.size}
        totalAmount={transactions
          .filter((t) => selectedTransactions.has(t.id))
          .reduce((sum, t) => sum + Math.abs(t.amount), 0)}
      />
    </div>
  );
};

export default Transactions;
